/**
 * `/rota` — the trainer-ops rota board (migration 149).
 *
 * One collection cycle per month: each trainer's submission moves
 * requested → reminded → submitted → confirmed → staged → uploaded (with
 * `escalated` / `skipped` as parking states). The page renders:
 *
 *   Overview  — cycle status, settled/uploaded/escalated counts, and the
 *               independent coverage verdict the `rota_coverage_watchdog`
 *               writes back — plus the collection calendar the agent runs on.
 *   Trainers  — one row per submission: stage, Telegram binding, reminders
 *               used, the parsed-schedule summary, slots-in-month (the TRUTH
 *               the ≥1-month-ahead guarantee is stated on), and the staged
 *               upload proposal's status when one exists.
 *   Timeline  — the cycle's newest events (asks, reminders, submissions,
 *               uploads, coverage checks, escalations).
 *
 * Read-only. Every mutation goes through the rota canonical actions the
 * trainer-ops agent + drain drive; approvals happen in Telegram. One
 * aggregate fetch, `GET /api/rota-board?month=YYYY-MM`.
 */

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  api,
  ApiError,
  type RotaBoardResponse,
  type RotaSubmission,
  type RotaSubmissionStage,
} from "../lib/api";
import { studioFormat } from "../lib/studio-tz";

const DATETIME_OPTS: Intl.DateTimeFormatOptions = {
  month: "short",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
};

function fmtDateTime(iso: string | null): string {
  if (iso === null) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : studioFormat(DATETIME_OPTS).format(d);
}

/** `YYYY-MM-DD` (a civil date, not an instant) → "20 Aug" / "September 2026". */
function civil(dateStr: string, opts: Intl.DateTimeFormatOptions): string {
  const d = new Date(`${dateStr}T12:00:00Z`);
  return Number.isNaN(d.getTime())
    ? dateStr
    : new Intl.DateTimeFormat("en-GB", { ...opts, timeZone: "UTC" }).format(d);
}

const MONTH_OPTS: Intl.DateTimeFormatOptions = { month: "long", year: "numeric" };
const DAY_OPTS: Intl.DateTimeFormatOptions = { day: "2-digit", month: "short" };

const STAGE_LABELS: Readonly<Record<RotaSubmissionStage, string>> = {
  requested: "Requested",
  reminded: "Reminded",
  submitted: "Submitted",
  confirmed: "Confirmed",
  staged: "Awaiting your tap",
  uploaded: "Uploaded",
  escalated: "Escalated",
  skipped: "Not working",
};

function OverviewStat({
  label,
  value,
}: {
  readonly label: string;
  readonly value: string | number;
}): JSX.Element {
  return (
    <div className="pr-stat">
      <div className="pr-stat-value">{value}</div>
      <div className="pr-stat-label">{label}</div>
    </div>
  );
}

function SubmissionRow({
  sub,
  proposalStatus,
}: {
  readonly sub: RotaSubmission;
  readonly proposalStatus: string | null;
}): JSX.Element {
  return (
    <tr>
      <td className="table-card-title">
        <Link to={`/trainers/${encodeURIComponent(sub.trainer_id)}`}>
          {sub.trainer_display_name}
        </Link>{" "}
        {!sub.trainer_telegram_bound ? (
          <span className="tag" title="No Telegram binding — issue a link">
            unbound
          </span>
        ) : null}
      </td>
      <td data-label="Stage">
        <span className="pr-type-badge">{STAGE_LABELS[sub.stage]}</span>{" "}
        {proposalStatus !== null ? (
          <span className="tag" title="Staged upload proposal">
            proposal: {proposalStatus}
          </span>
        ) : null}
      </td>
      <td data-label="Reminders" className="pr-num">{sub.reminder_count}/3</td>
      <td data-label="Slots in month" className="pr-num" title="Bookable slots inside the rota month">
        {sub.slots_in_month}
      </td>
      <td data-label="Summary" className="dc-muted">{sub.summary ?? "—"}</td>
      <td data-label="Updated" className="dc-muted">{fmtDateTime(sub.updated_at)}</td>
    </tr>
  );
}

export function Rota(): JSX.Element {
  const [data, setData] = useState<RotaBoardResponse | null>(null);
  const [month, setMonth] = useState<string | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .rotaBoard(month)
      .then((b) => {
        if (!cancelled) {
          setData(b);
          setError(null);
        }
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setError(e instanceof ApiError ? e.message : String(e));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [month]);

  if (loading && data === null) return <p className="empty">Loading rota…</p>;
  if (error !== null) {
    return <p className="empty">Couldn&apos;t load the rota ({error}).</p>;
  }
  if (data === null) return <p className="empty">No data.</p>;
  if (data.cycle === null) {
    return (
      <>
        <h1 className="page-title">Rota</h1>
        <p className="empty">
          No rota cycle yet — the trainer-ops agent opens one for next month on
          the 20th.
        </p>
      </>
    );
  }

  const { cycle, submissions, counts, proposals, timeline, months } = data;

  return (
    <>
      <div className="pr-section-head">
        <h1 className="page-title">
          Rota — {civil(cycle.month, MONTH_OPTS)}
        </h1>
        {months.length > 1 ? (
          <select
            value={month ?? cycle.month.slice(0, 7)}
            onChange={(e) => setMonth(e.target.value)}
            aria-label="Rota month"
          >
            {months.map((m) => (
              <option key={m} value={m.slice(0, 7)}>
                {civil(m, MONTH_OPTS)}
              </option>
            ))}
          </select>
        ) : null}
      </div>

      <div className="pr-overview">
        <OverviewStat label="Status" value={cycle.status} />
        <OverviewStat
          label="Settled"
          value={`${counts.total - counts.outstanding}/${counts.total}`}
        />
        <OverviewStat label="Uploaded" value={counts.uploaded} />
        <OverviewStat label="Escalated" value={counts.escalated} />
        <OverviewStat
          label="Coverage"
          value={
            cycle.coverage_ok === null
              ? "unchecked"
              : cycle.coverage_ok
                ? "OK"
                : "FAILING"
          }
        />
      </div>
      <p className="subtle pr-overview-note">
        Collection: opens {civil(cycle.opens_on, DAY_OPTS)} · reminders{" "}
        {civil(cycle.remind1_on, DAY_OPTS)} + {civil(cycle.remind2_on, DAY_OPTS)} ·
        escalate {civil(cycle.escalate_on, DAY_OPTS)} · deadline{" "}
        {civil(cycle.deadline_on, DAY_OPTS)}
        {cycle.coverage_checked_at !== null
          ? ` · coverage checked ${fmtDateTime(cycle.coverage_checked_at)}`
          : ""}
        . Slots-in-month is the truth the one-month-ahead guarantee is stated
        on; approvals happen in Telegram.
      </p>

      <div className="pr-section-head">
        <h2 className="pr-heading">Trainers</h2>
        <span className="subtle">
          {counts.outstanding} outstanding · {submissions.length} total
        </span>
      </div>
      {submissions.length === 0 ? (
        <p className="empty">No submissions in this cycle.</p>
      ) : (
        <div className="table-scroll-x">
          <table className="pr-people-table table-cards">
            <thead>
              <tr>
                <th>Trainer</th>
                <th>Stage</th>
                <th className="pr-num">Reminders</th>
                <th className="pr-num">Slots in month</th>
                <th>Summary</th>
                <th>Updated</th>
              </tr>
            </thead>
            <tbody>
              {submissions.map((sub) => (
                <SubmissionRow
                  key={sub.rota_submission_id}
                  sub={sub}
                  proposalStatus={
                    proposals[sub.rota_submission_id]?.status ?? null
                  }
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="pr-section-head">
        <h2 className="pr-heading">Timeline</h2>
        <span className="subtle">newest first</span>
      </div>
      {timeline.length === 0 ? (
        <p className="empty">No events yet.</p>
      ) : (
        <div className="table-scroll-x">
          <table className="pr-people-table table-cards">
            <tbody>
              {timeline.map((ev) => (
                <tr key={ev.id}>
                  <td data-label="When" className="dc-muted">{fmtDateTime(ev.at)}</td>
                  <td data-label="Kind">
                    <span className="pr-type-badge">{ev.kind}</span>
                  </td>
                  <td className="table-card-title">
                    {ev.summary}
                    {ev.detail !== null ? (
                      <details style={{ marginTop: 4 }}>
                        <summary
                          className="dc-muted"
                          style={{ cursor: "pointer", fontSize: "0.82rem" }}
                        >
                          Detail
                        </summary>
                        <div
                          style={{
                            marginTop: 4,
                            whiteSpace: "pre-wrap",
                            fontSize: "0.85rem",
                          }}
                        >
                          {ev.detail}
                        </div>
                      </details>
                    ) : null}
                  </td>
                  <td data-label="By" className="dc-muted">{ev.actor}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
